/**
 * @name: Product Card Image Swiper
 * @author: AI Agent
 * @date: 2026-09-16
 * @description: Shared infinite image carousel for product cards
 * @dependencies: swiper-bundle.min.js
 * @strict-mode: true
 */
(function () {
  'use strict';

  /**
   * @param {HTMLImageElement|null} img
   */
  function hydrateDeferredImage(img) {
    if (!img?.dataset?.src) return;

    img.src = img.dataset.src;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');

    for (const className of img.classList) {
      if (className.endsWith('__card-image--defer')) img.classList.remove(className);
    }
  }

  /**
   * @param {HTMLElement|null} card
   */
  function hydrateCardDeferredImages(card) {
    if (!card) return;
    card.querySelectorAll('img[data-src]').forEach(hydrateDeferredImage);
  }

  /**
   * @param {HTMLElement} swiperEl
   * @returns {{ card: HTMLElement|null, prevBtn: HTMLElement|null, nextBtn: HTMLElement|null }}
   */
  function findImageArrowButtons(swiperEl) {
    let card = swiperEl.parentElement;

    while (card) {
      for (const className of card.classList) {
        if (className.endsWith('__card')) {
          return {
            card,
            prevBtn: card.querySelector('[class*="__card-image-arrow--prev"]'),
            nextBtn: card.querySelector('[class*="__card-image-arrow--next"]'),
          };
        }
      }

      card = card.parentElement;
    }

    const scope = swiperEl.closest(
      '.boost-sd__product-item, .boost-sd__product-image, .boost-sd__suggestion-queries-item-product',
    );

    return {
      card: scope,
      prevBtn: scope?.querySelector('[class*="__card-image-arrow--prev"]') || null,
      nextBtn: scope?.querySelector('[class*="__card-image-arrow--next"]') || null,
    };
  }

  /**
   * @param {HTMLElement|null} prevBtn
   * @param {HTMLElement|null} nextBtn
   */
  function bindImageArrowGuards(prevBtn, nextBtn) {
    for (const button of [prevBtn, nextBtn]) {
      if (!button || button.dataset.arrowClickBound === 'true') continue;

      button.dataset.arrowClickBound = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
      });
    }
  }

  /**
   * @param {HTMLElement} swiperEl
   */
  function repairImageSwiperNavigation(swiperEl) {
    const swiperInstance = swiperEl.swiper;
    if (!swiperInstance?.navigation) return;

    const { prevBtn, nextBtn } = findImageArrowButtons(swiperEl);
    if (!prevBtn || !nextBtn) return;

    bindImageArrowGuards(prevBtn, nextBtn);
    if (swiperInstance.navigation.prevEl === prevBtn && swiperInstance.navigation.nextEl === nextBtn) return;

    swiperInstance.params.navigation.prevEl = prevBtn;
    swiperInstance.params.navigation.nextEl = nextBtn;
    swiperInstance.navigation.destroy();
    swiperInstance.navigation.init();
    swiperInstance.navigation.update();
  }

  /**
   * @param {ParentNode} [root]
   * @returns {object[]}
   */
  function initProductCardImageSwipers(root) {
    if (productImageSwipersPaused) {
      pendingSwiperInit = true;
      return [];
    }

    const container = root || document;
    const instances = [];

    container.querySelectorAll('[data-product-image-swiper]').forEach((swiperEl) => {
      if (!(swiperEl instanceof HTMLElement)) return;

      if (swiperEl.dataset.initialized === 'true') {
        repairImageSwiperNavigation(swiperEl);
        return;
      }

      if (typeof Swiper === 'undefined') {
        setTimeout(() => {
          instances.push(...initProductCardImageSwipers(container));
        }, 100);
        return;
      }

      const { card, prevBtn, nextBtn } = findImageArrowButtons(swiperEl);
      bindImageArrowGuards(prevBtn, nextBtn);
      swiperEl.dataset.initialized = 'true';

      const swiper = new Swiper(swiperEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: true,
        nested: true,
        allowTouchMove: true,
        preventClicks: false,
        preventClicksPropagation: false,
        navigation: {
          prevEl: prevBtn,
          nextEl: nextBtn,
        },
        on: {
          slideChange: function (instance) {
            const slide = instance.slides[instance.activeIndex];
            const img = slide?.querySelector('img');
            if (img) hydrateDeferredImage(img);
          },
        },
      });

      hydrateCardDeferredImages(card);
      if (productImageSwipersPaused) detachSwiperResize(swiper);
      instances.push(swiper);
    });

    return instances;
  }

  /**
   * Write Swiper's pixel sizes for the current card width.
   * While a filter toggle is in progress, `--aeSwiperShift` is the pre-toggle
   * translate divided by the pre-toggle width. CSS is still covering the
   * wrapper, so correct the inline translate to that ratio times the new
   * width before the cover is removed. A plain update() keeps the old pixel
   * translate and the photo jumps.
   */
  function fitProductImageSwipers() {
    document.querySelectorAll('[data-product-image-swiper]').forEach((swiperEl) => {
      if (!(swiperEl instanceof HTMLElement)) return;

      const swiper = swiperEl.swiper;
      if (!swiper || swiper.destroyed) return;

      try {
        const shift = Number.parseFloat(swiperEl.style.getPropertyValue('--aeSwiperShift'));
        swiper.setTransition(0);

        if (!Number.isFinite(shift)) {
          swiper.update();
          return;
        }

        swiper.updateSize();
        swiper.updateSlides();

        if (swiper.width) swiper.setTranslate(shift * swiper.width);

        swiper.updateProgress();
        swiper.updateSlidesClasses();
      } catch (err) {
        try {
          swiper.update();
        } catch (updateErr) {}
      }
    });
  }

  let productImageSwipersPaused = false;
  let pendingSwiperInit = false;

  /**
   * Swiper keeps its ResizeObserver private and emits `resize`, which measures
   * every card. Drop those listeners while the filter column is sliding.
   * @param {object} swiper
   */
  function detachSwiperResize(swiper) {
    if (!swiper.eventsListeners || swiper.__aeListeners || typeof swiper.off !== 'function') return;

    swiper.__aeListeners = {
      resize: Array.isArray(swiper.eventsListeners.resize) ? swiper.eventsListeners.resize.slice() : [],
      observerUpdate: Array.isArray(swiper.eventsListeners.observerUpdate) ? swiper.eventsListeners.observerUpdate.slice() : [],
    };
    swiper.off('resize observerUpdate');
  }

  /**
   * @param {{ eventsListeners?: Record<string, unknown[]>, __aeListeners?: Record<string, unknown[]> }} swiper
   */
  function attachSwiperResize(swiper) {
    if (!swiper.__aeListeners || !swiper.eventsListeners) return;

    swiper.eventsListeners.resize = swiper.__aeListeners.resize;
    swiper.eventsListeners.observerUpdate = swiper.__aeListeners.observerUpdate;
    delete swiper.__aeListeners;
  }

  /**
   * @param {boolean} paused
   */
  function setProductImageSwipersPaused(paused) {
    productImageSwipersPaused = paused;

    document.querySelectorAll('[data-product-image-swiper]').forEach((swiperEl) => {
      if (!(swiperEl instanceof HTMLElement)) return;

      const swiper = swiperEl.swiper;
      if (!swiper || swiper.destroyed) return;

      if (paused) {
        if (swiper.width) {
          swiperEl.style.setProperty('--aeSwiperShift', String(swiper.translate / swiper.width));
          swiperEl.classList.add('ae-swiper-fluid');
        }
        detachSwiperResize(swiper);
        return;
      }

      swiperEl.style.removeProperty('--aeSwiperShift');
      swiperEl.classList.remove('ae-swiper-fluid');
      attachSwiperResize(swiper);
    });

    if (!paused && pendingSwiperInit) {
      pendingSwiperInit = false;
      initProductCardImageSwipers();
    }
  }

  window.fitProductImageSwipers = fitProductImageSwipers;
  window.setProductImageSwipersPaused = setProductImageSwipersPaused;
  window.initProductImageSwipers = initProductCardImageSwipers;
  window.initProductShowcaseCardImageSwipers = initProductCardImageSwipers;
  window.initImageSwipers = initProductCardImageSwipers;

  const scheduleProductImageSwipers = () => {
    initProductCardImageSwipers();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(scheduleProductImageSwipers, 100);
    });
  } else {
    setTimeout(scheduleProductImageSwipers, 100);
  }

  if (window.__productCardImageSwiperObserver) return;

  window.__productCardImageSwiperObserver = new MutationObserver((mutations) => {
    let shouldInit = false;

    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.('[data-product-image-swiper]') || node.querySelector?.('[data-product-image-swiper]')) {
          shouldInit = true;
        }
      }
    }

    if (shouldInit) setTimeout(scheduleProductImageSwipers, 50);
  });

  if (document.body) {
    window.__productCardImageSwiperObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
})();
